import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Browser text-to-speech (Web Speech API).
 * @returns {{ speak, cancel, isSpeaking, hasSupport }}
 */
export function useSpeechSynthesis() {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [hasSupport, setHasSupport] = useState(false);
  const utteranceRef = useRef(null);
  const onEndRef = useRef(null);

  useEffect(() => {
    setHasSupport(typeof window !== 'undefined' && Boolean(window.speechSynthesis));
    return () => {
      try {
        window.speechSynthesis?.cancel();
      } catch {
        /* ignore */
      }
    };
  }, []);

  const cancel = useCallback(() => {
    onEndRef.current = null;
    try {
      window.speechSynthesis?.cancel();
    } catch {
      /* ignore */
    }
    setIsSpeaking(false);
  }, []);

  const speak = useCallback((text, { onEnd } = {}) => {
    const cleaned = String(text || '').replace(/\s+/g, ' ').trim();
    if (!cleaned || typeof window === 'undefined' || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
    } catch {
      /* ignore */
    }
    const utterance = new SpeechSynthesisUtterance(cleaned);
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.volume = 1;
    utteranceRef.current = utterance;
    onEndRef.current = typeof onEnd === 'function' ? onEnd : null;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      const cb = onEndRef.current;
      onEndRef.current = null;
      if (cb) cb();
    };
    utterance.onerror = () => {
      setIsSpeaking(false);
      onEndRef.current = null;
    };
    window.speechSynthesis.speak(utterance);
  }, []);

  return { speak, cancel, isSpeaking, hasSupport };
}
